import React, { useState, useEffect } from "react";
import { useDropzone } from "react-dropzone";
import "./FileDropzone.css";

const FileDropzone = ({
  oldFiles ,
  setFiles,
  updateOldImages,
  maxFiles = 5, // Default max files
  accept = "image/*", // Default accept all images
  maxSizeMB, // Optional per-file size limit (MB)
  imageOnly = true, // When false, allow non-image files (e.g. PDFs/docs)
}) => {
  const [acceptedFiles, setAcceptedFiles] = useState([]);

  const { getRootProps, getInputProps, open, isDragActive } = useDropzone({
    onDrop: (acceptedFilesList, rejectedFiles) => {
      // Check for rejected files
      if (rejectedFiles && rejectedFiles.length > 0) {
        alert(imageOnly
          ? 'Please upload only image files (JPG, PNG, etc.)'
          : 'Some files were rejected. Please check the allowed file types.');
        return;
      }

      // Validate file types
      const validFiles = imageOnly
        ? acceptedFilesList.filter(file => file.type.startsWith("image/"))
        : acceptedFilesList;
      if (validFiles.length !== acceptedFilesList.length) {
        alert('Please upload only image files');
        return;
      }

      // Validate file size when a limit is provided
      if (maxSizeMB && validFiles.some(file => file.size > maxSizeMB * 1024 * 1024)) {
        alert(`Each image must be ${maxSizeMB}MB or smaller.`);
        return;
      }

      // Check file limit
      if (acceptedFiles.length + validFiles.length > maxFiles) {
        alert(`You can only upload up to ${maxFiles} files`);
        return;
      }

      const filesWithPreviews = validFiles.map((file) => ({
        file,
        preview: file.type.startsWith("image/") ? URL.createObjectURL(file) : null,
      }));

      setAcceptedFiles((prev) => [...prev, ...filesWithPreviews]);
      setFiles((prev) => [...(Array.isArray(prev) ? prev : []), ...validFiles]);
    },
    multiple: true,
    accept: accept || "image/*", // Restrict to images only
    maxFiles: maxFiles, // Use prop value
  });

  // Clean up object URLs
  useEffect(() => {
    return () => {
      acceptedFiles.forEach((file) => {
        if (file.preview) URL.revokeObjectURL(file.preview);
      });
    };
  }, [acceptedFiles]);

  const removeFile = (index) => {
    const updatedFiles = [...acceptedFiles];
    URL.revokeObjectURL(updatedFiles[index].preview); // Cleanup
    updatedFiles.splice(index, 1);
    setAcceptedFiles(updatedFiles);
    setFiles(updatedFiles.map((f) => f.file));
  };

  const removeDBFile = (nameToRemove) => {
    const updatedOldImages = oldFiles.filter((image) => image !== nameToRemove);
    updateOldImages(updatedOldImages);
  };

  // File type icon renderer
  const renderFilePreview = (file) => {
    if (file.preview) {
      return <img src={file.preview} alt="Preview" className="preview-image" />;
    } else {
      return (
        <div className="file-icon">
          <i className="bi bi-file-pdf fs-1"></i>
          <span>{file.file.name}</span>
        </div>
      );
    }
  };

  return (
    <div className="dropzone-container">
      <div {...getRootProps()} className={`dropzone ${isDragActive ? "active" : ""}`}>
        <input {...getInputProps()} />
        <p>
          {isDragActive
            ? "Drop the files here..."
            : `Drag 'n' drop files here (Max ${maxFiles}), or click to select`}
        </p>
        <button type="button"  className="btn btn-sm btn-base">
          Select Files
        </button>
      </div>

      <div className="previews-container">
        {/* Old files (from DB) */}
        {oldFiles.map((image, index) => {
          const isFirstImage = index === 0 && acceptedFiles.length === 0;
          // Check if it's an image URL (has image extension or starts with http and likely an image)
          const isImage = typeof image === 'string' && (
            image.match(/\.(jpeg|jpg|gif|png|webp)(\?|$)/i) || 
            (image.startsWith('http') && !image.match(/\.(pdf|doc|docx)(\?|$)/i))
          );
          return (
            <div key={`old-${index}`} className={`preview-item cover-image ${isFirstImage ? 'cover-tag' : ''}`}>
              {isImage ? (
                <img src={image} alt="Preview" className="preview-image" />
              ) : (
                <a className="file-icon text-primary" href={image} target="_blank" rel="noopener noreferrer">
                  <i className="bi bi-file-pdf fs-1"></i>
                  <span>{image.split('/').pop()}</span>
                </a>
              )}
              <button onClick={() => removeDBFile(image)}  type="button" className="remove-button">
                <i className="bi  bi-x-lg fw-bold text-white"></i>
              </button>
            </div>
          );
        })}

        {/* Newly added files */}
        {acceptedFiles.map((file, index) => {
          const isFirstImage = oldFiles.length === 0 && index === 0;
          return (
            <div key={`new-${index}`} className={`preview-item cover-image ${isFirstImage ? 'cover-tag' : ''}`}>
              {renderFilePreview(file)}
              <button onClick={() => removeFile(index)} type="button" className="remove-button">
                  <i className="bi  bi-x-lg fw-bold text-white"></i>
              </button>
            </div>
          );
        })}
      </div>

  
    </div>
  );
};

export default FileDropzone;